<script setup lang="ts">
// Round-25 (2026-08-31, user feedback): "one shared table-side design across Home and
// LiveMatch" — the boxing-corner box chrome (border weights, tint, top-edge accent, corner dot,
// selection/glow rings) previously lived as two near-identical copies of Tailwind classes: one
// inline in TableDrawing.vue (per-side <button>, cornerTheme prop) and one inline in
// LiveMatch.vue (per-side score card <div>). This component is the single source of truth for
// that chrome; both callers wrap their own (very different) content — TableDrawing's
// avatar/name/select-pill/clear-X vs LiveMatch's avatar/name/score/±buttons/elo — in a
// <TableSide> instead of duplicating the box styling. Red = LEFT, blue = RIGHT is a hard
// invariant enforced here by `side` alone.
//
// Two callers, two structural shapes though, and that split is real, not laziness: LiveMatch
// deliberately dropped the felt table in round-5 ("the user has both it and the cards and only
// wants the cards") in favour of standalone cards spaced by a grid gap. TableDrawing still IS a
// felt table with a net divider — its two sides are flush against each other and the felt's own
// outer border, not independent floating cards. `inset` captures exactly that structural
// difference (small margin + own rounding so the corner box reads the same as a LiveMatch card
// while still living inside the felt panel) — everything else (color, tint opacity, border
// weight, dot, rings) is identical code, not just identical values, for both shapes.
//
// Layout/interaction classes (tap-target, transition, flex sizing, gap, padding) are NOT owned
// here — callers pass them as a plain `class` on the <TableSide> tag and Vue's normal attribute
// fallthrough merges them onto the root element, same as `@click`/`type="button"` do. This
// component only ever decides the corner theming.
import { computed } from 'vue'
import type { Side } from '../types'

const props = withDefaults(
  defineProps<{
    side: Side
    /** Root tag — TableDrawing's sides are tappable buttons, LiveMatch's cards are plain divs. */
    as?: 'div' | 'button'
    /** Red/blue corner theming. Off = fully neutral (NewMatch's TableDrawing usage, unchanged). */
    theme?: boolean
    /** True for TableDrawing (embedded in a continuous felt table — needs its own margin/
     *  rounding to read as a corner box); false for LiveMatch's already-gapped card grid. */
    inset?: boolean
    /** Small corner indicator dot from LiveMatch's card design. Defaults to `theme`. */
    showDot?: boolean
    /** Amber pulsing selection ring (New match's active-side indicator). */
    active?: boolean
    /** Transient ring+tint boost for a just-resolved voice corner-claim. */
    glow?: boolean
  }>(),
  { as: 'div', theme: true, inset: false, showDot: undefined, active: false, glow: false },
)

const dotVisible = computed(() => props.showDot ?? props.theme)

// Literal class strings (not string-interpolated `bg-${color}-...`) on purpose — Tailwind's JIT
// scanner needs the class names to appear verbatim in source to generate them, same pattern the
// rest of the codebase already uses for side-conditional classes (MicPulse's glowColorClass,
// TableDrawing/LiveMatch's own pre-refactor ternaries).
const boxClasses = computed(() => {
  const classes: string[] = ['relative flex flex-col items-center justify-center overflow-hidden']

  if (props.theme) {
    const corner =
      props.side === 'left'
        ? 'bg-red-950/25 border-red-900/40 border-t-red-500/70'
        : 'bg-blue-950/25 border-blue-900/40 border-t-blue-500/70'
    if (props.inset) {
      classes.push('m-1 rounded-xl border-2 border-t-4', corner)
    } else {
      classes.push('rounded-2xl border-2 border-t-4', corner)
    }
  }

  if (props.active) classes.push('ring-4 ring-inset ring-amber-400 animate-pulse')
  if (props.glow) {
    classes.push(props.side === 'left' ? 'ring-4 ring-inset ring-red-500/70 bg-red-500/10' : 'ring-4 ring-inset ring-blue-500/70 bg-blue-500/10')
  }

  return classes
})
</script>

<template>
  <component :is="as" :class="boxClasses">
    <span
      v-if="dotVisible"
      class="absolute top-2 w-2.5 h-2.5 rounded-full"
      :class="side === 'left' ? 'left-2 bg-red-500/80' : 'right-2 bg-blue-500/80'"
      :title="side === 'left' ? 'Red corner' : 'Blue corner'"
    />
    <slot />
  </component>
</template>
